import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usd, pct, num } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, ProvenanceTag, TableSkeleton, VerdictBadge } from "@/components/common/primitives";
import {
  SlidersHorizontal, Columns3, Download, Save, X, TrendingUp, TrendingDown, Minus,
  ArrowUpDown, Bookmark, Eye, ScanSearch, ChevronRight, ChevronLeft, AlertTriangle, Inbox,
} from "lucide-react";
import { useUiState } from "@/lib/prefs";
import { toast } from "sonner";

const PRESETS = {
  discovery: { label: "Discovery", cols: ["title", "item_id", "category", "sales_signal", "est_sales", "trend", "price", "avg90", "stability", "sellers", "wfs", "retail", "rating", "reviews", "review_growth", "opportunity", "risk"] },
  profitability: { label: "Profitability", cols: ["title", "source_cost", "price", "net_profit", "roi", "margin", "est_sales", "opportunity"] },
  competition: { label: "Competition", cols: ["title", "sellers", "wfs", "retail", "stability", "opportunity", "risk"] },
  advanced: { label: "Advanced", cols: ["title", "item_id", "category", "est_sales", "trend", "price", "avg90", "sellers", "wfs", "rating", "reviews", "review_growth", "net_profit", "roi", "opportunity", "risk"] },
};
const COL_DEF = {
  title: { label: "Product", align: "left" }, item_id: { label: "Walmart Item ID", mono: true },
  category: { label: "Category" }, sales_signal: { label: "WM signal" },
  est_sales: { label: "Est. sales/mo", num: true, est: true }, trend: { label: "Trend" },
  price: { label: "Price", money: true }, avg90: { label: "90d avg", money: true },
  stability: { label: "Stability" }, sellers: { label: "Sellers", num: true },
  wfs: { label: "WFS", num: true }, retail: { label: "WM Retail", bool: true },
  rating: { label: "Rating", num: true }, reviews: { label: "Reviews", num: true },
  review_growth: { label: "Rev growth" }, opportunity: { label: "Opp. score", num: true, score: true },
  risk: { label: "Risk" }, source_cost: { label: "Source cost", money: true },
  net_profit: { label: "Net profit", money: true }, roi: { label: "ROI", pctv: true }, margin: { label: "Margin", pctv: true },
};
const RISK_CLR = { low: "text-emerald-600 bg-emerald-100", medium: "text-amber-600 bg-amber-100", high: "text-red-600 bg-red-100" };
const TrendIcon = ({ t }) => t === "up" ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : t === "down" ? <TrendingDown className="h-4 w-4 text-red-600" /> : <Minus className="h-4 w-4 text-ink-muted" />;

function Cell({ row, col }) {
  const d = COL_DEF[col]; const v = row[col];
  if (col === "trend") return <TrendIcon t={v} />;
  if (col === "risk") return <span className={cn("text-micro font-mono uppercase px-1.5 py-0.5 rounded", RISK_CLR[v])}>{v}</span>;
  if (col === "retail") return v ? <span className="text-micro font-mono text-orange-500">Yes</span> : <span className="text-micro font-mono text-ink-muted">No</span>;
  if (col === "opportunity") return <span className={cn("font-mono font-semibold", v >= 80 ? "text-emerald-600" : v >= 65 ? "text-amber-600" : "text-ink-secondary")}>{v}</span>;
  if (col === "title") return <span className="text-ink-primary">{v}</span>;
  if (d.money) return <span className="font-mono">{usd(v)}</span>;
  if (d.pctv) return <span className={cn("font-mono", v >= 30 ? "text-emerald-600" : "text-amber-600")}>{pct(v)}</span>;
  if (d.num) return <span className="font-mono">{typeof v === "number" ? num(v) : v}</span>;
  if (d.mono) return <span className="ident text-micro">{v}</span>;
  return <span className="text-ink-secondary">{v}</span>;
}

export default function TopSellers() {
  const [rows, setRows] = useState(null);
  const [preset, setPreset] = useState("discovery");
  const [sort, setSort] = useState({ key: "est_sales", dir: "desc" });
  const [drawer, setDrawer] = useState(false);
  const [preview, setPreview] = useState(null);
  const [category, setCategory] = useState("All");
  const [oneVariation, setOneVariation] = useState(false);
  const [filters, setFilters] = useState({ minRoi: 0, maxSellers: 99, excludeRetail: false, minSales: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [density, setDensity] = useState("comfortable");
  const [selected, setSelected] = useState(new Set());
  const uiVal = useUiState();
  const nav = useNavigate();

  useEffect(() => { api.topSellers().then((r) => setRows(r.data)); }, []);

  useEffect(() => {
    const lock = drawer || !!preview;
    document.body.style.overflow = lock ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawer, preview]);

  useEffect(() => {
    if (!drawer && !preview) return;
    const onKey = (e) => { if (e.key === "Escape") { setDrawer(false); setPreview(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer, preview]);

  const categories = useMemo(() => ["All", ...Array.from(new Set((rows || []).map((r) => r.category)))], [rows]);

  const chips = [];
  if (filters.minRoi > 0) chips.push(["Min ROI " + filters.minRoi + "%", () => setFilters((f) => ({ ...f, minRoi: 0 }))]);
  if (filters.maxSellers < 99) chips.push(["Max sellers " + filters.maxSellers, () => setFilters((f) => ({ ...f, maxSellers: 99 }))]);
  if (filters.excludeRetail) chips.push(["Exclude WM Retail", () => setFilters((f) => ({ ...f, excludeRetail: false }))]);
  if (filters.minSales > 0) chips.push(["Min sales " + filters.minSales, () => setFilters((f) => ({ ...f, minSales: 0 }))]);

  const view = useMemo(() => {
    let r = (rows || []).filter((x) => x.roi >= filters.minRoi && x.sellers <= filters.maxSellers && x.est_sales >= filters.minSales && (!filters.excludeRetail || !x.retail) && (category === "All" || x.category === category));
    r = [...r].sort((a, b) => { const m = sort.dir === "asc" ? 1 : -1; return a[sort.key] > b[sort.key] ? m : a[sort.key] < b[sort.key] ? -m : 0; });
    if (oneVariation) {
      const seen = new Set();
      r = r.filter((x) => { const g = x.group || x.id; if (seen.has(g)) return false; seen.add(g); return true; });
    }
    return r;
  }, [rows, filters, sort, category, oneVariation]);

  const rowPad = density === "compact" ? "py-1.5" : "py-2.5";
  const pageCount = Math.max(1, Math.ceil(view.length / pageSize));
  const paged = view.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [category, preset, filters, oneVariation, pageSize, sort]);
  const allOnPage = paged.length > 0 && paged.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); allOnPage ? paged.forEach((r) => n.delete(r.id)) : paged.forEach((r) => n.add(r.id)); return n; });
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const loadingState = !rows || uiVal === "loading";
  const errorState = uiVal === "error";
  const emptyState = uiVal === "empty" || (rows && view.length === 0);

  const cols = PRESETS[preset].cols;
  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div className="min-w-0 max-w-full">
      <PageHeader title="Top-Selling Products"
        description="Walmart best-seller research. Estimates are labeled and not exact Walmart figures."
        meta={<><ProvenanceTag kind="fixture" /><span className="text-micro font-mono text-ink-muted">Updated 12m ago · {view.length} results</span></>}
        actions={<div className="flex flex-wrap items-center gap-1.5">
          <button onClick={() => setDrawer(true)} data-testid="open-filters" className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact flex items-center gap-1.5 hover:bg-surface-2"><SlidersHorizontal className="h-4 w-4" />Filter</button>
          <button onClick={() => toast("Configure columns (fixture)")} className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact flex items-center gap-1.5 hover:bg-surface-2"><Columns3 className="h-4 w-4" />Columns</button>
          <button onClick={() => toast.success("Exported (fixture)")} className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact flex items-center gap-1.5 hover:bg-surface-2"><Download className="h-4 w-4" />Export</button>
          <button onClick={() => toast.success("Search saved (fixture)")} className="h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact flex items-center gap-1.5 ez-press"><Save className="h-4 w-4" />Save search</button>
        </div>} />

      {/* category pills (Keepa best-sellers style) */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {categories.map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`category-${c.toLowerCase()}`}
            className={cn("px-2.5 h-7 rounded-full text-micro font-mono transition-colors",
              category === c ? "ez-seg-active rounded-full" : "ez-seg ez-seg-outline rounded-full text-ink-secondary")}>{c}</button>
        ))}
        <label className="w-full sm:w-auto sm:ml-auto flex items-center gap-1.5 text-micro font-mono text-ink-secondary cursor-pointer">
          <input type="checkbox" data-testid="one-variation-toggle" checked={oneVariation} onChange={(e) => setOneVariation(e.target.checked)} className="h-3.5 w-3.5 shrink-0" />
          Show only one variation per product
        </label>
      </div>

      {/* toolbar: presets + time modes */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll">
          {Object.entries(PRESETS).map(([k, v]) => (
            <button key={k} onClick={() => setPreset(k)} data-testid={`preset-${k}`}
              className={cn("px-3 h-8 text-micro font-mono", preset === k ? "ez-seg-active" : "ez-seg text-ink-secondary")}>{v.label}</button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll ml-auto">
          {["Current", "30D", "90D", "180D", "All"].map((t, i) => (
            <button key={t} className={cn("px-2.5 h-8 text-micro font-mono", i === 2 ? "ez-seg-active" : "ez-seg")}>{t}</button>
          ))}
        </div>
      </div>

      {/* active filter chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {chips.map(([label, clear], i) => (
            <span key={i} className="inline-flex items-center gap-1 text-micro font-mono bg-surface-2 rounded-ctrl pl-2 pr-1 py-0.5">
              {label}<button onClick={clear} aria-label={`Remove ${label}`} className="hover:text-red-600"><X className="h-3 w-3" /></button>
            </span>
          ))}
          <button onClick={() => setFilters({ minRoi: 0, maxSellers: 99, excludeRetail: false, minSales: 0 })} className="text-micro font-mono text-signal-blue">Clear all</button>
        </div>
      )}

      {/* batch toolbar */}
      {selected.size > 0 && (
        <div data-testid="batch-toolbar" className="flex items-center gap-2 mb-2 px-3 h-11 rounded-panel bg-navy-900 text-ink-inverse">
          <span className="text-compact font-medium">{selected.size} selected</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => toast.success(`${selected.size} leads saved (demo)`)} className="h-8 px-3 rounded-ctrl bg-emerald-600 text-white text-micro ez-press">Save leads</button>
            <button onClick={() => toast.success(`Exported ${selected.size} (demo)`)} className="h-8 px-3 rounded-ctrl border border-ink-inverse/20 text-micro">Export</button>
            <button data-testid="batch-clear" onClick={() => setSelected(new Set())} className="h-8 px-3 rounded-ctrl border border-ink-inverse/20 text-micro">Clear</button>
          </div>
        </div>
      )}

      {/* density + result status */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-micro font-mono text-ink-muted">{view.length} results</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-micro font-mono text-ink-muted">Density</span>
          <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro font-mono">
            {["comfortable", "compact"].map((dz) => (
              <button key={dz} data-testid={`density-${dz}`} onClick={() => setDensity(dz)} className={cn("px-2.5 h-7 capitalize", density === dz ? "ez-seg-active" : "ez-seg")}>{dz}</button>
            ))}
          </div>
        </div>
      </div>

      <div data-testid="top-sellers-table" className="min-w-0 border border-edge-subtle rounded-panel bg-surface-0 overflow-hidden">
        {errorState ? (
          <div className="p-12 text-center" data-testid="ts-error"><AlertTriangle className="h-7 w-7 text-red-600 mx-auto mb-2" /><div className="text-compact text-ink-primary">Couldn't load the research grid.</div><button onClick={() => window.location.reload()} className="mt-3 h-8 px-4 rounded-ctrl bg-emerald-600 text-white text-compact">Retry</button></div>
        ) : loadingState ? (
          <div className="p-4"><TableSkeleton rows={pageSize > 12 ? 12 : pageSize} cols={7} /></div>
        ) : emptyState ? (
          <div className="p-12 text-center" data-testid="ts-empty"><Inbox className="h-7 w-7 text-ink-muted mx-auto mb-2" /><div className="text-compact text-ink-muted">No products match your filters.</div></div>
        ) : (
          <div className="overflow-x-auto ez-scroll">
            <table className="w-full text-compact">
              <thead><tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2 border-b border-edge-subtle">
                <th className="py-2 px-3 w-8"><input type="checkbox" data-testid="select-all" checked={allOnPage} onChange={toggleAll} className="h-3.5 w-3.5 align-middle" /></th>
                {cols.map((c) => (
                  <th key={c} className={cn("py-2 px-3 whitespace-nowrap", COL_DEF[c].align === "left" || c === "title" ? "text-left sticky left-8 bg-surface-2" : "text-right")}>
                    <button onClick={() => toggleSort(c)} className="inline-flex items-center gap-1 hover:text-ink-primary">
                      {COL_DEF[c].label}{COL_DEF[c].est && <span className="text-amber-600">*</span>}<ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </th>
                ))}
                <th className="px-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {paged.map((row) => (
                  <tr key={row.id} onClick={() => setPreview(row)} data-testid={`ts-row-${row.id}`}
                    className={cn("border-b border-edge-subtle last:border-0 hover:bg-surface-2 cursor-pointer", selected.has(row.id) && "bg-emerald-100/40")}>
                    <td className={cn(rowPad, "px-3")} onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`select-${row.id}`} checked={selected.has(row.id)} onChange={() => toggleOne(row.id)} className="h-3.5 w-3.5 align-middle" /></td>
                    {cols.map((c) => (
                      <td key={c} className={cn(rowPad, "px-3 whitespace-nowrap", c === "title" ? "text-left sticky left-8 bg-surface-0 min-w-[220px]" : "text-right")}>
                        <Cell row={row} col={c} />
                      </td>
                    ))}
                    <td className="px-3 text-right"><ChevronRight className="h-4 w-4 text-ink-muted inline" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* footer: pagination */}
      {!loadingState && !errorState && !emptyState && (
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <div className="flex items-center gap-1.5 text-micro font-mono text-ink-muted">Rows
            <select data-testid="page-size" value={pageSize} onChange={(e) => setPageSize(+e.target.value)} className="h-7 rounded-ctrl border border-edge-subtle bg-surface-0 px-1.5">
              {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <span className="text-micro font-mono text-ink-muted">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, view.length)} of {view.length}</span>
          <div className="ml-auto flex items-center gap-1">
            <button data-testid="page-prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className={cn("h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle", page === 1 && "opacity-40 cursor-not-allowed")}><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-micro font-mono px-2">Page {page} / {pageCount}</span>
            <button data-testid="page-next" disabled={page === pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} className={cn("h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle", page === pageCount && "opacity-40 cursor-not-allowed")}><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      <p className="text-micro font-mono text-ink-muted mt-2">* Estimated — not an exact Walmart figure. Hover columns for methodology (fixture).</p>

      {/* Filter slide-over drawer */}
      {drawer && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/40 flex" onClick={() => setDrawer(false)}>
          <div role="dialog" aria-modal="true" aria-label="Filters" className="ml-auto h-full w-full max-w-sm ez-glass-strong flex flex-col ez-rise-in" onClick={(e) => e.stopPropagation()} data-testid="filter-drawer">
            <div className="h-14 flex items-center justify-between px-4 border-b border-edge-subtle bg-surface-0">
              <span className="font-medium text-ink-primary">Advanced filters</span>
              <button onClick={() => setDrawer(false)} aria-label="Close filters" className="text-ink-muted h-8 w-8 grid place-items-center">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto ez-scroll p-4 space-y-4">
              <label className="block"><span className="text-micro font-mono uppercase text-ink-muted">Min ROI: {filters.minRoi}%</span>
                <input type="range" min="0" max="80" value={filters.minRoi} onChange={(e) => setFilters((f) => ({ ...f, minRoi: +e.target.value }))} className="w-full mt-1" /></label>
              <label className="block"><span className="text-micro font-mono uppercase text-ink-muted">Max seller count: {filters.maxSellers}</span>
                <input type="range" min="1" max="15" value={Math.min(15, filters.maxSellers)} onChange={(e) => setFilters((f) => ({ ...f, maxSellers: +e.target.value }))} className="w-full mt-1" /></label>
              <label className="block"><span className="text-micro font-mono uppercase text-ink-muted">Min monthly sales: {filters.minSales}</span>
                <input type="range" min="0" max="1500" step="50" value={filters.minSales} onChange={(e) => setFilters((f) => ({ ...f, minSales: +e.target.value }))} className="w-full mt-1" /></label>
              <label className="flex items-center justify-between py-1"><span className="text-compact text-ink-primary">Exclude Walmart Retail</span>
                <input type="checkbox" checked={filters.excludeRetail} onChange={(e) => setFilters((f) => ({ ...f, excludeRetail: e.target.checked }))} className="h-4 w-4" /></label>
            </div>
            <div className="p-3 border-t border-edge-subtle flex gap-2">
              <button onClick={() => setFilters({ minRoi: 0, maxSellers: 99, excludeRetail: false, minSales: 0 })} className="flex-1 h-9 rounded-ctrl border border-edge-subtle text-compact">Reset</button>
              <button onClick={() => setDrawer(false)} className="flex-1 h-9 rounded-ctrl bg-signal-blue text-white text-compact">Apply</button>
            </div>
          </div>
        </div>, document.body)}

      {/* Row preview drawer */}
      {preview && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/40 flex" onClick={() => setPreview(null)}>
          <div role="dialog" aria-modal="true" aria-label="Product preview" className="ml-auto h-full w-full max-w-md ez-glass-strong flex flex-col ez-rise-in" onClick={(e) => e.stopPropagation()} data-testid="preview-drawer">
            <div className="h-14 flex items-center justify-between px-4 border-b border-edge-subtle bg-surface-0">
              <span className="font-medium text-ink-primary truncate">{preview.title}</span>
              <button onClick={() => setPreview(null)} aria-label="Close preview" className="text-ink-muted h-8 w-8 grid place-items-center">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto ez-scroll p-4 space-y-3">
              <div className="text-micro font-mono text-ink-muted">{preview.brand} · {preview.item_id} · {preview.category}</div>
              <div className="grid grid-cols-3 gap-2">
                {[["Opp. score", preview.opportunity], ["Est. sales/mo", num(preview.est_sales)], ["Sellers", `${preview.sellers} (${preview.wfs} WFS)`], ["Profit", usd(preview.net_profit)], ["ROI", pct(preview.roi)], ["Price", usd(preview.price)]].map(([l, v]) => (
                  <div key={l} className="rounded-ctrl border border-edge-subtle p-2 text-center"><div className="text-micro font-mono uppercase text-ink-muted">{l}</div><div className="font-mono font-semibold text-ink-primary">{v}</div></div>
                ))}
              </div>
              <div className="rounded-ctrl border border-edge-subtle p-2.5">
                <div className="text-micro font-mono uppercase text-ink-muted mb-1">Main risks</div>
                <div className="text-compact text-ink-secondary">{preview.retail ? "Walmart Retail present · " : ""}{preview.risk === "high" ? "High seller concentration / volatile price" : preview.risk === "medium" ? "Moderate competition" : "Low competition"}</div>
              </div>
              <div className="rounded-ctrl bg-surface-2 p-2.5 text-micro font-mono text-ink-muted">Quick calc: source {usd(preview.source_cost)} → net {usd(preview.net_profit)} · {pct(preview.roi)} ROI (fixture)</div>
            </div>
            <div className="p-3 border-t border-edge-subtle flex gap-2">
              <button onClick={() => toast.success("Lead saved (fixture)")} className="h-9 px-3 rounded-ctrl border border-emerald-600/40 text-emerald-600 text-compact flex-1"><Bookmark className="h-4 w-4 inline mr-1" />Save</button>
              <button onClick={() => toast("Watching (fixture)")} className="h-9 px-3 rounded-ctrl border border-amber-600/40 text-amber-600 text-compact flex-1"><Eye className="h-4 w-4 inline mr-1" />Watch</button>
              <button onClick={() => { setPreview(null); nav("/app/analyzer"); }} data-testid="open-analyzer" className="h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact flex-1"><ScanSearch className="h-4 w-4 inline mr-1" />Analyze</button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
